import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import moment from 'moment';
import { useAuth } from '../../Common/Configurations/AuthContext';
import apiService from '../../Common/Configurations/APIService';
import config from '../../Common/Configurations/APIConfig';
import './MyFlightBookingDetails.css'; // You can reuse the styles

// Helper component for Star Ratings
const StarRating = ({ rating }) => {
    const totalStars = 5;
    let stars = [];
    for (let i = 1; i <= totalStars; i++) {
        if (i <= rating) {
            stars.push(<i key={i} className="ri-star-s-fill text-warning"></i>);
        } else {
            stars.push(<i key={i} className="ri-star-s-line text-muted"></i>);
        }
    }
    return <div className="star-rating">{stars}</div>;
};

const MyHotelBookingDetails = () => {
    const { hotelBookingId } = useParams();
    const { user } = useAuth();
    const [bookingDetails, setBookingDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const getBookingDetails = async () => {
            if (!hotelBookingId) {
                setError("Booking ID not found.");
                setLoading(false);
                return;
            }

            try {
                const response = await apiService.get(`${config.GETHOTELBOOKINGDETAILS}/${hotelBookingId}`);
                console.log("Hotel booking details response:", response);

                if (response.data) {
                    setBookingDetails(response.data);
                } else {
                    throw new Error("Invalid data structure in API response.");
                }

            } catch (err) {
                setError("Failed to load booking details. Please try again.");
                console.error("Error fetching booking details:", err);
            } finally {
                setLoading(false);
            }
        };

        getBookingDetails();
    }, [hotelBookingId]);

    const renderStatusBadge = (status) => {
        if (!status) return null;
        let badgeClass = 'bg-secondary';
        switch (status.toUpperCase()) {
            case 'CONFIRMED': case 'SUCCESS': badgeClass = 'bg-success'; break;
            case 'HOLD': case 'PENDING': badgeClass = 'bg-warning text-dark'; break;
            case 'CANCELLED': case 'FAILED': badgeClass = 'bg-danger'; break;
            default: break;
        }
        return <span className={`badge ${badgeClass}`}>{status}</span>;
    };

    if (loading) {
        return <div className="text-center p-5"><div className="spinner-border"></div><p className="mt-2">Loading Booking Details...</p></div>;
    }

    if (error) {
        return <div className="container mt-4"><div className="alert alert-danger">{error}</div></div>;
    }

    if (!bookingDetails) {
        return <div className="container mt-4"><p>No booking details found.</p></div>;
    }

    const { hotelDetails, hotelRoomDetails, hotelGuestInfos } = bookingDetails;

    return (
        <div className="main-content">
            <div className="booking-details-wrapper">
                <div className="container">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h2 className="details-title mb-0">Hotel Booking Details</h2>
                        <Link to="/my-hotelbookings" className="btn btn-outline-secondary">
                            <i className="ri-arrow-left-line me-2"></i>Back to Bookings
                        </Link>
                    </div>

                    {/* Main Hotel Info Card */}
                    <div className="card itinerary-card mb-4">
                        <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <h5 className="mb-0">{hotelDetails?.hotelName}</h5>
                            <div className="d-flex align-items-center gap-3">
                                <span><strong className="text-muted">Booking: </strong>{renderStatusBadge(bookingDetails.bookingStatus)}</span>
                                <span><strong className="text-muted">Payment: </strong>{renderStatusBadge(bookingDetails.paymentStatus)}</span>
                                <div className="d-flex gap-2 d-print-none">
                                    <Link to={`/hotel/voucher/${hotelBookingId}`} className="btn btn-sm btn-primary shadow-none">Voucher</Link>
                                    <Link to={`/hotel/invoice/${hotelBookingId}`} className="btn btn-sm btn-primary shadow-none">Invoice</Link>
                                </div>
                            </div>
                        </div>
                        <div className="card-body">
                            <StarRating rating={hotelDetails?.rating} />
                            <h4 className="mt-2">{hotelDetails?.hotelName}</h4>
                            <p className="text-muted"><i className="ri-map-pin-line me-2"></i>{hotelDetails?.address}, {hotelDetails?.cityName}, {hotelDetails?.countryName}</p>
                            <hr />
                            <div className="d-flex justify-content-around text-center">
                                <div>
                                    <small className="text-muted d-block">Check-in</small>
                                    <strong>{moment(bookingDetails.checkIn).format('ddd, DD MMM YYYY')}</strong>
                                </div>
                                <div>
                                    <small className="text-muted d-block">Check-out</small>
                                    <strong>{moment(bookingDetails.checkOut).format('ddd, DD MMM YYYY')}</strong>
                                </div>
                                <div>
                                    <small className="text-muted d-block">Guests</small>
                                    <strong>{bookingDetails.noOfGuests}</strong>
                                </div>
                                <div>
                                    <small className="text-muted d-block">Rooms</small>
                                    <strong>{bookingDetails.noOfRooms}</strong>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row g-4">
                        {/* Left Column: Guest & Room Details */}
                        <div className="col-lg-7">
                            <div className="card details-card mb-4">
                                <div className="card-header"><h6 className="mb-0">Guest & Room Information</h6></div>
                                <div className="card-body">
                                    <h6 className="text-muted">Guest(s)</h6>
                                    <ul className="list-group list-group-flush mb-3">
                                        {hotelGuestInfos?.[0]?.adultsFields.map((guest, i) => (
                                            <li key={`adult-${i}`} className="list-group-item">
                                                <i className="ri-user-line me-2"></i>{guest.title}. {guest.firstName} {guest.lastName}
                                            </li>
                                        ))}
                                    </ul>
                                    <h6 className="text-muted">Room Details</h6>
                                    <ul className="list-group list-group-flush">
                                        <li className="list-group-item d-flex justify-content-between"><span>Room Type</span><strong>{hotelRoomDetails?.requestedHotels?.[0]?.roomType}</strong></li>
                                        <li className="list-group-item d-flex justify-content-between"><span>Meal Plan</span><strong>{hotelRoomDetails?.requestedHotels?.[0]?.mealType}</strong></li>
                                        <li className="list-group-item d-flex justify-content-between"><span>Cancellation</span><strong className="text-capitalize">{hotelRoomDetails?.bookingType?.toLowerCase()}</strong></li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Right Column: Price & Booking Summary */}
                        <div className="col-lg-5">
                            <div className="card details-card">
                                <div className="card-header"><h6 className="mb-0">Confirmation</h6></div>
                                <div className="card-body">
                                    <ul className="list-group list-group-flush">
                                        <li className="list-group-item d-flex justify-content-between"><span>Booking No:</span><strong>{bookingDetails.bookingNo}</strong></li>
                                        <li className="list-group-item d-flex justify-content-between"><span>Booking Date:</span><strong>{moment(bookingDetails.bookingDate).format('DD MMM YYYY')}</strong></li>
                                    </ul>
                                </div>
                            </div>
                            <div className="card details-card">
                                <div className="card-header"><h6 className="mb-0">Price Summary</h6></div>
                                <div className="card-body">
                                    <ul className="list-group list-group-flush">
                                        <li className="list-group-item d-flex justify-content-between"><span>Net Fare</span><span>{bookingDetails.netPrice?.toFixed(2)}</span></li>
                                        <li className="list-group-item d-flex justify-content-between"><span>Convenience Fee (Including GST)</span><span>{bookingDetails.convenienceFee?.toFixed(2)}</span></li>
                                        <li className="list-group-item d-flex justify-content-between"><span>GST</span><span>{bookingDetails.gstAmount?.toFixed(2)}</span></li>
                                        <li className="list-group-item d-flex justify-content-between bg-light">
                                            <strong className="text-primary">Total Price</strong>
                                            <strong className="text-primary fs-5">{bookingDetails.totalPrice?.toFixed(2)} INR</strong>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MyHotelBookingDetails;